import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { TEAM_INVITES_PATHNAME, TEAM_PATHNAME } from '@/shared/constants/pathnames';
import type { AppNotification, NotificationCategory, NotificationType } from '@/shared/lib/notifications/notification';
import { markAllNotificationsRead } from '@/shared/lib/notifications/mark-all-notifications-read';
import { markNotificationRead } from '@/shared/lib/notifications/mark-notification-read';
import { selectItems, useNotificationsStore } from '@/shared/lib/notifications/store';


// куда ведёт клик по типу события — точнее категории, различает, например, инвайт и удаление из команды
const TYPE_HREFS: Partial<Record<NonNullable<NotificationType>, string>> = {
    team_invite_received: TEAM_INVITES_PATHNAME,
    team_removed        : TEAM_PATHNAME,
};

// запасной вариант для старых записей без type (null) и незнакомых кодов
const CATEGORY_HREFS: Partial<Record<NotificationCategory, string>> = {
    team: TEAM_INVITES_PATHNAME,
};


export const useNotificationsMenu = (closeMenu: () => void) => {
    const router = useRouter();
    const items         = useNotificationsStore(selectItems);
    const markReadLocal = useNotificationsStore((s) => s.markRead);
    const markAllLocal  = useNotificationsStore((s) => s.markAllRead);
    const markUnread    = useNotificationsStore((s) => s.markUnread);

    const hasItems  = items.length > 0;
    const hasUnread = items.some((n) => !n.read);

    // оптимистично отмечаем в сторе, при ошибке API возвращаем непрочитанным
    const persistRead = useCallback((id: string) => {
        markNotificationRead(id).catch(() => markUnread(id));
    }, [ markUnread ]);

    const markRead = useCallback((id: string) => {
        const item = items.find((n) => n.id === id);

        if (!item || item.read) return;

        markReadLocal(id);

        if (item.remote) persistRead(id);
    }, [ items, markReadLocal, persistRead ]);

    // оптимистично отмечаем всё в сторе; при ошибке API откатываем только то, что реально было непрочитанным
    const markAllRead = useCallback(() => {
        const hasUnreadRemote  = items.some((n) => !n.read && n.remote);
        const previouslyUnread = items.filter((n) => !n.read).map((n) => n.id);

        markAllLocal();

        if (!hasUnreadRemote) return;

        markAllNotificationsRead().catch(() => previouslyUnread.forEach(markUnread));
    }, [ items, markAllLocal, markUnread ]);

    const openNotification = useCallback((notification: AppNotification) => {
        markRead(notification.id);

        const href = (notification.type && TYPE_HREFS[notification.type]) || CATEGORY_HREFS[notification.category];

        if (!href) return;

        closeMenu();
        router.push(href);
    }, [ closeMenu, markRead, router ]);

    return {
        hasItems,
        hasUnread,
        items,
        markAllRead,
        openNotification,
    };
};
